import React, { useState, useCallback } from 'react';
import SVG from 'react-inlinesvg';
import { useAdminTranslation } from '@/admin/hooks/useAdminTranslation';

import { UserData, UserValidationError } from '@/school-management/types/user-data';
import ValidateUser from '@/school-management/utils/userDataValidationUtils';
import { sanitizeEmail } from '@/admin/utils/validations';
import classNames from '../UsersTable.module.scss';
import binIcon from '@/admin/assets/svg/bin.svg';

interface AddUserRowProps {
  setUsersData: React.Dispatch<React.SetStateAction<UserData[]>>;
  setShowNewUserInput: React.Dispatch<React.SetStateAction<boolean>>;
}

const AddUserRow: React.FC<AddUserRowProps> = ({ setUsersData, setShowNewUserInput }) => {
  const { t } = useAdminTranslation();
  const [newUser, setNewUser] = useState<UserData>({
    firstName: '',
    lastName: '',
    email: '',
  });
  const [validationErrors, setValidationErrors] = useState<UserValidationError[]>([]);
  const [touchedFields, setTouchedFields] = useState<Record<string, boolean>>({
    firstName: false,
    lastName: false,
    email: false,
  });

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const sanitizedValue = name === 'email' ? sanitizeEmail(value) : value;
    setNewUser(prev => ({ ...prev, [name]: sanitizedValue }));
  }, []);

  const handleAddNewUser = useCallback(() => {
    const errors = ValidateUser(newUser);
    if (errors.length) {
      setValidationErrors(errors);
      setTouchedFields({
        firstName: true,
        lastName: true,
        email: true,
      });
      return;
    }

    setUsersData(prev => [...prev, newUser]);
    resetNewUser();
  }, [newUser, setUsersData]);

  const resetNewUser = useCallback(() => {
    setNewUser({ firstName: '', lastName: '', email: '' });
    setTouchedFields({ firstName: false, lastName: false, email: false });
    setShowNewUserInput(false);
    setValidationErrors([]);
  }, [setShowNewUserInput]);

  const handleCloseAddNewUser = useCallback(() => {
    resetNewUser();
  }, [resetNewUser]);

  const findErrorMessage = useCallback(
    (field: string): string | undefined => {
      if (!touchedFields[field]) return undefined;
      const error = validationErrors.find(error => error.field === field);
      return error ? error.message : undefined;
    },
    [validationErrors, touchedFields]
  );

  const handleBlur = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      const { name } = e.target;
      setTouchedFields(prev => ({ ...prev, [name]: true }));

      const fieldErrors = ValidateUser(newUser).filter(error => error.field === name);
      setValidationErrors(prevErrors => {
        const otherErrors = prevErrors.filter(error => error.field !== name);
        return [...otherErrors, ...fieldErrors];
      });
    },
    [newUser]
  );

  return (
    <tr
      className={`${classNames.studentRow} ${validationErrors.length > 0 ? classNames.errorRow : ''}`}
    >
      {['firstName', 'lastName', 'email'].map(field => (
        <td key={field}>
          <input
            type={field === 'email' ? 'email' : 'text'}
            name={field}
            value={newUser[field]}
            onBlur={handleBlur}
            className={findErrorMessage(field) ? classNames.errorInput : ''}
            onChange={handleChange}
          />
          {findErrorMessage(field) && (
            <span className={classNames.rowError}>
              {t(`settings.add_students.errors.${findErrorMessage(field)}`)}
            </span>
          )}
        </td>
      ))}
      <td className={classNames.actionIcons}>
        <div>
          <button
            className={classNames.saveButton}
            onClick={handleAddNewUser}
            aria-label={t('common.save').toString()}
          >
            {t('common.save')}
          </button>
          <button
            className={classNames.buttonSvg}
            onClick={handleCloseAddNewUser}
            aria-label={t('bimloaFilter.close').toString()}
          >
            <SVG src={binIcon} className={classNames.closingBtn} />
          </button>
        </div>
      </td>
    </tr>
  );
};

export default AddUserRow;
